/**
 * Uploading an expense attachment.
 *
 * Extracted out of ExpenseEditor so the attach-first panel on a new record and
 * the ไฟล์แนบ section at the bottom share one implementation — two copies of this
 * would drift, and they both have to produce exactly the same file[] entry shape
 * that the old system still reads.
 */
import { uploadFile } from './uploadDirect.js'
import { generateThumbnail } from './thumbnail.js'
import { periodFields } from './expense.js'

/** Accounting-period folder ('YYYY-MM') the object is filed under. */
export function periodFolder(dateLike) {
  const p = periodFields(dateLike)
  const y = p.expense_year || new Date().getFullYear()
  const m = p.expense_month_num || new Date().getMonth() + 1
  return `${y}-${String(m).padStart(2, '0')}`
}

function uid() {
  return (globalThis.crypto?.randomUUID?.())
    || 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2)
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = reject
    r.readAsDataURL(blob)
  })
}

/**
 * Upload one file and return the `file[]` entry to append to the record.
 * Upload and thumbnail generation run in parallel — the thumbnail is stored
 * inline as a data URI, matching what the record already holds.
 */
export async function uploadExpenseFile(file, ym) {
  // Straight to Wasabi where the bucket allows it, through the old proxy where it
  // does not — src/lib/uploadDirect.js. The bytes bypassing the Worker is what
  // keeps a big scanned invoice from exhausting the isolate every /api route shares.
  const [up, thumbBlob] = await Promise.all([
    uploadFile('/api/expense-upload', { ym }, file),
    generateThumbnail(file),
  ])

  return {
    location: up.location,
    name: up.name,
    url: up.url || '',
    thumbnail: thumbBlob ? await blobToDataURL(thumbBlob) : '',
    type: up.type,
    size: up.size,
    tag: [],
    note: '',
    presigned_expired: up.presigned_expired ?? null,
    created_at: up.created_at ?? null,
    modified_at: up.modified_at ?? null,
    employee_id: up.employee_id ?? 0,
    uuid: up.uuid || uid(),
  }
}
